import React, { useContext, useState, useEffect } from 'react';

import {
    AppBar,
    CssBaseline,
    Divider,
    Drawer,
    Hidden,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Tooltip,
    LinearProgress
} from '@material-ui/core';

import {
    CrumbsBar
} from '../common';

import { TableMenu } from './TableMenu';

import { AuthContext } from '../contexts/AuthContext';
import { NavContext, NavRoutes } from '../contexts/NavContext';
import { UserContext } from '../contexts/UserContext';
import { MenuContext } from '../contexts/MenuContext';

import { ApplicationRoles, useBusyState } from '../../helpers';

import MenuIcon from '@material-ui/icons/Menu';
import LockIcon from '@material-ui/icons/LockOutlined';
import ExitToAppIcon from '@material-ui/icons/ExitToAppOutlined';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { makeStyles, useTheme } from '@material-ui/core/styles';
require("../customcss/customcss.css");

const drawerWidth = 540;
const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
    },
    drawer: {
        // width: drawerWidth,
        // flexShrink: 0
    },
    appBar: {
        backgroundColor: "#f5f2f0",
    },
    menuButton: {
        //marginRight: theme.spacing(2),
    },
    toolbarButton: {
        margin: theme.spacing(1),
        verticalAlign: "text-bottom"
    },
    drawerHeader: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(0, 1),
        justifyContent: "flex-end",
        // necessary for content to be below app bar
        ...theme.mixins.Toolbar,
        height: "64px"
    },
    drawerSpacer: {
        // necessary for content to be below app bar
        ...theme.mixins.Toolbar,
        height: "64px"
    },
    drawerPaper: {
        width: drawerWidth,
        backgroundColor: '#f5f2f0'
    },
    crumbs: {
        marginLeft: theme.spacing(2)
    },
    content: {
        flexGrow: 1,
        [theme.breakpoints.up("sm")]: {
            padding: theme.spacing(3),
        },
        [theme.breakpoints.down("xs")]: {
            padding: theme.spacing(1),
        },
        // necessary for content to be below app bar
        ...theme.mixins.Toolbar
    },
    title: {
        flexGrow: 1
    },
    user: {
        margin: theme.spacing(1)
    },
    userName: {
        fontWeight: 600
    }
}));

export const Layout = ({ ...props }) => {
    const classes = useStyles();
    const theme = useTheme();

    const {
        menuItems,
        navigateTo,
        switchTo
    } = useContext(NavContext);

    const { container } = props;

    const {
        getAccountInfo,
        logOut
    } = useContext(AuthContext);

    const {
        getMeAsync,
        isUserInRole,
        isUserInRoles,
        state: { me: userInfo }
    } = useContext(UserContext);

    const {
        state: { menuIsOpen },
        setMenuIsOpen
    } = useContext(MenuContext);

    const [isReady, setIsReady] = useState(false);
    const loadCurrentUserAsync = async () => {
        setIsBusy({ getMe: true });
        await getMeAsync();
        setIsBusy({ getMe: false });
        setIsReady(true);
    }

    const drawerToggleHandler = () => {
        setMenuIsOpen(!menuIsOpen);
    };

    const itemClickHandler = (item) => {
        navigateTo(item.routePath);
        drawerToggleHandler();
    }

    const logoutClickHandler = () => {
        logOut();
    }

    const goToAdminClickHandler = () => {
        navigateTo(NavRoutes.Admin.Dashboard);
    }

    const tableSelectedHandler = (table) => {
        drawerToggleHandler();
        switchTo(NavRoutes.TableContent, [table.tableId]);
    }

    const accountInfo = getAccountInfo();
    const [busy, isBusy, setIsBusy] = useBusyState({});
    const [username, setUsername] = useState(accountInfo.userName);
    const [userFullName, setUserFullName] = useState(accountInfo.name);

    useEffect(() => {
        loadCurrentUserAsync();
    }, []);

    const drawerContent = () => {
        return (
            <>
                <div className={classes.drawerHeader}>
                    <IconButton onClick={drawerToggleHandler}>
                        {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </div>
                <Divider />
                <List>
                    {menuItems && menuItems
                        .filter(item => (!item.isAdmin || item.isAdmin === false)
                            && (item.roles === null || item.roles === undefined || isUserInRoles(item.roles)))
                        .map((item, index) =>
                            <ListItem
                                key={index}
                                button
                                onClick={() => itemClickHandler(item)}>
                                <ListItemIcon>
                                    {item.icon !== undefined ? item.icon : <MenuIcon />}
                                </ListItemIcon>
                                <ListItemText primary={item.title || item.routePath} />
                            </ListItem>
                        )}
                </List>
                <TableMenu onItemSelected={tableSelectedHandler} />
            </>
        );
    }

    return (

        <div className={classes.root}>
            {userInfo &&
                <>
                    <CssBaseline />
                    <AppBar position="fixed" className={classes.appBar}>
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={drawerToggleHandler}
                                className={classes.menuButton}>
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h5">
                                Supply Chain Business Metadata&nbsp;
                            </Typography>
                            <Typography variant="h6" noWrap className={classes.title}>
                                <CrumbsBar className={classes.crumbs} />
                            </Typography>
                            <Hidden smDown>
                                <Typography variant="subtitle2" component="span" noWrap className={classes.user}>
                                    Welcome <span className={ classes.userName }>{userFullName}!</span>
                                </Typography>
                            </Hidden>
                            {isUserInRoles([ApplicationRoles.Administrator, ApplicationRoles.ProcessOwner, ApplicationRoles.DataQuality]) &&
                                <Tooltip title="Admin Dashboard">
                                    <IconButton
                                        aria-label="admin area"
                                        className={classes.toolbarButton}
                                        color="inherit"
                                        onClick={goToAdminClickHandler}>
                                        <LockIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                            }
                            <IconButton
                                aria-label="logout"
                                className={classes.toolbarButton}
                                color="inherit"
                                onClick={logoutClickHandler}
                                >
                                <Tooltip title="Logout">
                                    <span>
                                        <ExitToAppIcon color="primary" />
                                    </span>
                                </Tooltip>
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <nav className={classes.drawer} aria-label="menu items">
                        {/* Mobile Screens */}
                        <Hidden lgUp implementation="js">
                            <Drawer
                                container={container}
                                variant="temporary"
                                anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                                open={menuIsOpen}
                                onClose={drawerToggleHandler}
                                classes={{
                                    paper: classes.drawerPaper,
                                }}
                                ModalProps={{
                                    // Better open performance on mobile.
                                    keepMounted: true,
                                }}>
                                {drawerContent()}
                            </Drawer>
                        </Hidden>
                        {/* Desktop Screens */}
                        <Hidden mdDown implementation="js">
                            <Drawer
                                variant="temporary"
                                classes={{
                                    paper: classes.drawerPaper,
                                }}
                                open={menuIsOpen}
                                onClose={drawerToggleHandler}>
                                {drawerContent()}
                            </Drawer>
                        </Hidden>
                    </nav>
                </>
            }
            <main className={classes.content}>
                {userInfo &&
                    <div className={classes.drawerSpacer} />
                }
                {isBusy() && <LinearProgress />}
                {isReady && props.children}
            </main>
        </div>
    );
}